import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Button } from '@gapartners/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setFocusedContact } from '@/store/slices/callSlice';
import { setCaseInfo } from '@/store/slices/caseSlice';
import { setCustomerInfo } from '@/store/slices/customerSlice';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { ConfirmDiscardChangesDialog } from '@/components/contact-center/ConfirmDiscardChangesDialog';
import {
  getInitialOrCaptureContactId,
  hasActiveCallLeg,
  isChatContact,
} from '@/components/amazon-connect/contactUtils';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import type { ConnectContact } from '@/types';

function getSessionContactId(contact: ConnectContact): string | null {
  try {
    return connectAdapter.getCallScopeContactId(contact);
  } catch {
    try {
      return getInitialOrCaptureContactId(contact) ?? contact.getContactId();
    } catch {
      return null;
    }
  }
}

function getContactQueueName(contact: ConnectContact): string | null {
  try {
    return contact.getQueue?.()?.name?.trim() || null;
  } catch {
    return null;
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return [hours, minutes, seconds]
      .map(value => value.toString().padStart(2, '0'))
      .join(':');
  }

  return [minutes, seconds]
    .map(value => value.toString().padStart(2, '0'))
    .join(':');
}

function getContactDurationMs(startedAt?: number | null): number | null {
  if (!startedAt) return null;
  return Date.now() - startedAt;
}

export function SessionSwitcher() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { activeContacts, pendingFinishContacts, finishPendingContact } =
    useAmazonConnectContacts();
  const { focusedContactId, sessions } = useAppSelector(state => state.call);
  const unsavedEditsContactId = useAppSelector(
    state => state.case.unsavedEditsContactId
  );
  const [, setDurationTick] = useState(0);
  const [finishConfirmContact, setFinishConfirmContact] =
    useState<ConnectContact | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDurationTick(tick => tick + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const sessionContacts = useMemo(() => {
    const byId = new Map<string, ConnectContact>();
    for (const contact of activeContacts) {
      if (!hasActiveCallLeg(contact)) continue;
      const contactId = getSessionContactId(contact);
      if (contactId) byId.set(contactId, contact);
    }
    return Array.from(byId.entries());
  }, [activeContacts]);

  const pendingFinishEntries = useMemo(() => {
    const byId = new Map<string, ConnectContact>();
    for (const contact of pendingFinishContacts) {
      const contactId = getSessionContactId(contact);
      if (contactId) byId.set(contactId, contact);
    }
    return Array.from(byId.entries());
  }, [pendingFinishContacts]);

  // Finishing clears the contact's case, which drops unsaved case drawer edits.
  const handleFinish = (contactId: string, contact: ConnectContact) => {
    if (unsavedEditsContactId === contactId) {
      setFinishConfirmContact(contact);
      return;
    }
    finishPendingContact(contact);
  };

  const handleDiscardAndFinish = () => {
    if (finishConfirmContact) finishPendingContact(finishConfirmContact);
    setFinishConfirmContact(null);
  };

  if (sessionContacts.length <= 1 && pendingFinishEntries.length === 0) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {t('components.contactCenter.sessionSwitcher.title', 'Active sessions')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {sessionContacts.map(([contactId, contact]) => {
          const session = sessions[contactId];
          const isChat = session?.type === 'chat' || isChatContact(contact);
          const isSelected = focusedContactId === contactId;
          const unreadCount = session?.unreadCount ?? 0;
          const fallbackName = getContactQueueName(contact);
          const name =
            session?.callerName ||
            session?.callerPhoneNumber ||
            fallbackName ||
            contactId.slice(0, 8);
          const prefix = isChat
            ? t('components.contactCenter.sessionSwitcher.chat', 'Chat')
            : t('components.contactCenter.sessionSwitcher.call', 'Call');
          const unreadLabel =
            unreadCount > 0
              ? t(
                  'components.contactCenter.sessionSwitcher.unread',
                  '{{count}} unread',
                  { count: unreadCount }
                )
              : null;
          const durationMs = getContactDurationMs(session?.startedAt);
          const durationLabel =
            durationMs != null ? formatDuration(durationMs) : null;
          const companyName =
            session?.currentCustomer?.companyName ||
            fallbackName ||
            t(
              'components.contactCenter.sessionSwitcher.noCompany',
              'No company'
            );
          const statusLabel = isSelected
            ? t('components.contactCenter.sessionSwitcher.active', 'Active')
            : unreadLabel ||
              t('components.contactCenter.sessionSwitcher.waiting', 'Waiting');
          const barWidth = isSelected ? '65%' : unreadCount > 0 ? '45%' : '25%';

          return (
            <Box
              key={contactId}
              component="button"
              type="button"
              onClick={() => {
                dispatch(setFocusedContact(contactId));
                dispatch(setCustomerInfo(session?.currentCustomer ?? null));
                dispatch(setCaseInfo(session?.currentCase ?? null));
              }}
              sx={{
                width: { xs: '100%', sm: 240 },
                minHeight: 92,
                p: 1.5,
                border: 1,
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderLeft: 4,
                borderLeftColor: isSelected ? 'error.main' : 'transparent',
                borderRadius: 1.5,
                bgcolor: isSelected ? 'background.paper' : 'grey.50',
                cursor: 'pointer',
                textAlign: 'left',
                opacity: isSelected ? 1 : 0.72,
                transition: 'border-color 120ms ease, opacity 120ms ease',
                '&:hover': {
                  opacity: 1,
                  borderColor: isSelected ? 'primary.main' : 'grey.400',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, display: 'flex', gap: 1 }}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      color: isChat ? 'secondary.main' : 'primary.main',
                      fontSize: '1rem',
                      lineHeight: 1.4,
                    }}
                  >
                    {isChat ? '●' : '☎'}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="text.primary"
                      noWrap
                    >
                      {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {companyName}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={isSelected ? 'success.main' : 'warning.main'}
                    sx={{ textTransform: 'uppercase', display: 'block' }}
                  >
                    {statusLabel}
                  </Typography>
                  {durationLabel && (
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.primary"
                      sx={{ display: 'block' }}
                    >
                      {durationLabel}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ mt: 1.25 }}>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 999,
                    bgcolor: 'grey.200',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: barWidth,
                      height: '100%',
                      bgcolor: isSelected ? 'success.main' : 'warning.main',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                >
                  {prefix}
                </Typography>
              </Box>
            </Box>
          );
        })}
        {pendingFinishEntries.map(([contactId, contact]) => {
          const session = sessions[contactId];
          const isChat = session?.type === 'chat' || isChatContact(contact);
          const fallbackName = getContactQueueName(contact);
          const name =
            session?.callerName ||
            session?.callerPhoneNumber ||
            fallbackName ||
            contactId.slice(0, 8);
          const prefix = isChat
            ? t('components.contactCenter.sessionSwitcher.chat', 'Chat')
            : t('components.contactCenter.sessionSwitcher.call', 'Call');
          const finishLabel = isChat
            ? t('components.amazonConnect.finishAfterChatWork')
            : t('components.amazonConnect.finishAfterCallWork');

          return (
            <Box
              key={`pending-${contactId}`}
              sx={{
                width: { xs: '100%', sm: 240 },
                minHeight: 92,
                p: 1.5,
                border: 1,
                borderColor: 'warning.main',
                borderLeft: 4,
                borderLeftColor: 'warning.main',
                borderRadius: 1.5,
                bgcolor: '#fefce8',
                textAlign: 'left',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    color: isChat ? 'secondary.main' : 'primary.main',
                    fontSize: '1rem',
                    lineHeight: 1.4,
                  }}
                >
                  {isChat ? '●' : '☎'}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="text.primary"
                    noWrap
                  >
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {prefix}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1.25 }}>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  fullWidth
                  onClick={() => handleFinish(contactId, contact)}
                >
                  {finishLabel}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
      <ConfirmDiscardChangesDialog
        open={finishConfirmContact != null}
        message={t('components.contactCenter.unsavedCaseChangesFinishMessage')}
        discardLabel={t('components.contactCenter.discardAndFinish')}
        onDiscard={handleDiscardAndFinish}
        onKeepEditing={() => setFinishConfirmContact(null)}
      />
    </Paper>
  );
}
